import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface OperationLog {
  id: string;
  operator_username: string;
  operator_display_name: string;
  operator_role: string;
  module: string;
  action: string;
  target_name: string;
  detail: string;
  created_at: string;
}

const MODULE_OPTIONS = ['全部模块', '系统', '首页轮播', '核心业务', 'AI产品', '客户案例', '新闻动态', '用户管理'];
const ACTION_OPTIONS = ['全部操作', '登录', '退出', '新增', '编辑', '删除', '排序', '禁用', '启用', '修改密码'];

const ACTION_COLOR: Record<string, string> = {
  新增: 'bg-teal-100 text-teal-700',
  编辑: 'bg-sky-100 text-sky-700',
  删除: 'bg-red-100 text-red-500',
  排序: 'bg-amber-100 text-amber-700',
  登录: 'bg-green-100 text-green-700',
  退出: 'bg-gray-100 text-gray-500',
  禁用: 'bg-orange-100 text-orange-600',
  启用: 'bg-emerald-100 text-emerald-700',
  修改密码: 'bg-purple-100 text-purple-600',
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function LogsPage() {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('全部模块');
  const [actionFilter, setActionFilter] = useState('全部操作');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('admin_operation_logs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (moduleFilter !== '全部模块') query = query.eq('module', moduleFilter);
    if (actionFilter !== '全部操作') query = query.eq('action', actionFilter);
    if (operatorFilter.trim()) query = query.ilike('operator_username', `%${operatorFilter.trim()}%`);

    const { data, count, error } = await query;
    if (!error && data) {
      setLogs(data);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, moduleFilter, actionFilter, operatorFilter]);

  useEffect(() => {
    setPage(1);
  }, [moduleFilter, actionFilter, operatorFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">操作日志</h1>
          <p className="text-sm text-gray-500 mt-0.5">记录所有管理员的后台操作行为</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-database-2-line text-gray-400"></i>
          </div>
          共 {total} 条记录
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-filter-3-line text-sm"></i>
          </div>
          <span className="text-sm text-gray-500">筛选：</span>
        </div>
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 cursor-pointer"
        >
          {MODULE_OPTIONS.map((m) => <option key={m}>{m}</option>)}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 cursor-pointer"
        >
          {ACTION_OPTIONS.map((a) => <option key={a}>{a}</option>)}
        </select>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input
            type="text"
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            placeholder="搜索操作人..."
            className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 w-40"
          />
        </div>
        {(moduleFilter !== '全部模块' || actionFilter !== '全部操作' || operatorFilter) && (
          <button
            onClick={() => { setModuleFilter('全部模块'); setActionFilter('全部操作'); setOperatorFilter(''); }}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            清除筛选
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>
            加载中...
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mb-3">
              <i className="ri-file-history-line text-4xl text-gray-300"></i>
            </div>
            <p className="text-sm">暂无操作记录</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">操作人</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">模块</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">操作</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">对象</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">详情</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">时间</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr
                    key={log.id}
                    className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${
                      idx === logs.length - 1 ? 'border-b-0' : ''
                    }`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-100 text-teal-700 font-bold text-xs flex-shrink-0">
                          {(log.operator_display_name || log.operator_username).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            {log.operator_display_name || log.operator_username}
                          </div>
                          <div className="text-xs text-gray-400">
                            {log.operator_role === 'super_admin' ? '超级管理员' : '编辑员'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-gray-700">{log.module}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          ACTION_COLOR[log.action] ?? 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-gray-700 max-w-[160px] truncate block" title={log.target_name}>
                        {log.target_name || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-gray-500 max-w-[200px] truncate block" title={log.detail}>
                        {log.detail || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  第 {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} 条，共 {total} 条
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-base"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                          p === page
                            ? 'bg-teal-600 text-white font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
